import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { FormArray, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import { OrderDetailInput } from '@generated/graphql';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, map, startWith, switchMap, tap } from 'rxjs';
import { Job } from '@modules/job/models/job.model';
import { JobFeeForm, JobFeeItemForm } from '@modules/job/components/dialog/job-fees-dialog/job-fees.form';
import { NumericInputFormatterDirective } from '@shared/directives/numeric-input-formatter/numeric-input-formatter.directive';
import { SharePipesModule } from '@shared/share-pipes/share-pipes.module';
import { JobApi } from '@modules/job/api/job.api';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { ScreenSpinnerService } from '@shared/component/full-screen-spinner/screen-spinner.service';
import {
  ConfirmDialogAction,
  ConfirmDialogComponent,
  ConfirmDialogComponentData
} from '@shared/component/dialog/confirm-dialog/confirm-dialog.component';
import {cloneDeep} from "lodash-es";

type AmendFeePreviewRequest = {
  services: OrderDetailInput[];
  signature: string;
};

export interface JobFeesDialogData {
  job: Job;
}

@UntilDestroy()
@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatIconModule,
    NumericInputFormatterDirective,
    SharePipesModule,
  ],
  providers: [JobFeeForm],
  selector: 'app-job-fees-dialog',
  templateUrl: './job-fees-dialog.component.html',
  styleUrls: ['./job-fees-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobFeesDialogComponent implements OnInit {
  @ViewChild('confirmBillingDialogTemplate') confirmBillingDialogTemplate!: TemplateRef<any>;
  invalidReasons: string[] = [];
  hasFeeChanges = false;
  private initialFeeSignature = '';

  constructor(
    private dialogRef: MatDialogRef<JobFeesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: JobFeesDialogData,
    public form: JobFeeForm,
    private matDialog: MatDialog,
    private jobApi: JobApi,
    private snackBarService: SnackBarService,
    private screenSpinnerService: ScreenSpinnerService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    const {job} = this.data;

    this.form.setJobDetails(
      job.supportedCountry?.currency || 'SGD',
      job.supportedCountry?.taxRate || 0,
      job.supportedCountry?.includeTax || false,
    );

    if (job.services?.length) {
      job.services.forEach((item) => {
        const i = this.form.createByOrderDetail(item);

        i.onRateChanges$().pipe(
          untilDestroyed(this)
        ).subscribe()
      });
    }

    this.initialFeeSignature = this.buildFeeInputSignature();
    this.hasFeeChanges = false;

    this.form.items.valueChanges
      .pipe(
        untilDestroyed(this),
        startWith(this.form.items.getRawValue()),
        tap(() => {
          this.form.calculateTotals();
          this.updateInvalidReasons();
          this.hasFeeChanges = this.buildFeeInputSignature() !== this.initialFeeSignature;
          this.cdr.markForCheck();
        }),
        map(() => this.buildPreviewRequest()),
        debounceTime(300),
        distinctUntilChanged((previous, current) => previous?.signature === current?.signature),
        switchMap((request) => {
          if (!request) {
            this.clearBillingPreviewErrors();
            this.cdr.markForCheck();

            return EMPTY;
          }

          return this.loadAmendFeePreview$(request.services);
        }),
      )
      .subscribe();

    // Initial check for invalid reasons and mark all fields as touched to show errors
    this.updateInvalidReasons();
    this.form.markAllAsTouched();
  }

  get items(): FormArray<JobFeeItemForm> {
    return this.form.items;
  }

  onQuantityBlur(control: FormControl<number>): void {
    const currentValue = control.value;
    if (typeof currentValue === 'number' && !isNaN(currentValue)) {
      const roundedValue = Math.round(currentValue);
      // Only patch value if it actually changed after rounding to avoid unnecessary events
      if (roundedValue !== currentValue) {
        control.patchValue(roundedValue, { emitEvent: true });
      }
    }
  }

  private updateInvalidReasons(): void {
    this.invalidReasons = [];
    if (this.form.invalid) {
      this.form.items.controls.forEach((itemForm) => {
        if (itemForm.invalid) {
          const serviceName = itemForm.controls.name.value || 'Unnamed Service';

          // Custom validation for rate
          if (itemForm.hasError('atLeastOneFeePositive')) {
            this.invalidReasons.push(`The 'Rate' for the service: '${serviceName}' must be greater than 0.`);
          }

          // Check for rate errors
          if (itemForm.controls.rate.hasError('required')) {
            this.invalidReasons.push(`Please enter a 'Rate' for the service: '${serviceName}'.`);
          } else if (itemForm.controls.rate.hasError('min')) {
            this.invalidReasons.push(`The 'Rate' for the service '${serviceName}' cannot be negative.`);
          }

          // Check for quantity errors
          if (itemForm.controls.quantity.hasError('required')) {
            this.invalidReasons.push(`Please enter a 'Quantity' for the service: '${serviceName}'.`);
          } else if (itemForm.controls.quantity.hasError('min')) {
            this.invalidReasons.push(`The 'Quantity' for the service '${serviceName}' cannot be a negative value.`);
          } else if (itemForm.controls.quantity.hasError('integer')) {
            this.invalidReasons.push(`The 'Quantity' for the service '${serviceName}' must be a whole number.`);
          }

          // Check for name errors
          if (itemForm.controls.name.hasError('required')) {
            this.invalidReasons.push(`A 'Service Name' is missing for one of the items. Please ensure all services have a name.`);
          }
        }
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.form.valid && this.hasFeeChanges) {
      const confirmDialogRef = this.matDialog.open<ConfirmDialogComponent, ConfirmDialogComponentData, ConfirmDialogAction>(ConfirmDialogComponent, {
        width: 'auto',
        data: {
          title: 'Confirm Billing',
          template: this.confirmBillingDialogTemplate,
          confirmBtnTxt: 'Yes',
          cancelBtnTxt: 'Cancel',
        },
      });

      confirmDialogRef.afterClosed().pipe(
        switchMap(result => {
          if (result === ConfirmDialogAction.Confirm) {
            const updatedOrderDetails = this.form.items.controls.map(itemForm => {
              const originalOrderDetail = cloneDeep(itemForm.controls.orderDetail.value);

              delete originalOrderDetail.service.service.serviceCategory;

              const { orderDetail, rate, quantity } = itemForm.value

              return {
                quantity,
                service: {
                  id: orderDetail.service.id,
                  serviceFee: rate,
                },
              } as OrderDetailInput;
            });

            return this.jobApi.amendFee$(this.data.job.id, updatedOrderDetails).pipe(
              this.screenSpinnerService.withSpinner(),
              tap(job => {
                if (job) {
                  this.snackBarService.pushSuccessAlert('Job fees updated successfully!');
                  this.dialogRef.close(true); // Close with true on success
                } else {
                  this.snackBarService.pushErrorMessage();
                  this.dialogRef.close(false); // Close with false on error
                }
              }),
              catchError(e => {
                this.snackBarService.pushErrorMessage();
                this.dialogRef.close(false); // Close with false on error
                return EMPTY;
              })
            );
          }
          return EMPTY; // User cancelled the confirmation dialog
        }),
        untilDestroyed(this)
      ).subscribe();
    } else {
      this.form.markAllAsTouched();
    }
  }

  get billingPreviewValidationErrorMessage(): string | null {
    const error = this.form?.getError('stripeChargeValidation') as {
      reason: string;
    } | null;

    return error?.reason ?? null;
  }

  private canLoadBillingPreview(): boolean {
    return this.items.controls.every((itemForm) => (
      itemForm.controls.rate.valid
      && itemForm.controls.quantity.valid
      && !itemForm.hasError('atLeastOneFeePositive')
      && !!itemForm.controls.orderDetail.value?.service?.id
    ));
  }

  private buildFeeInputSignature(): string {
    return this.form.items.controls.map((itemForm) => {
      const { orderDetail, rate, quantity } = itemForm.getRawValue();

      return `${orderDetail?.service?.id ?? ''}:${rate}:${quantity}`;
    }).join('|');
  }

  private buildPreviewRequest(): AmendFeePreviewRequest | null {
    if (!this.canLoadBillingPreview()) {
      return null;
    }

    const services = this.form.items.controls.map((itemForm) => {
      const { orderDetail, rate, quantity } = itemForm.getRawValue();

      return {
        quantity,
        service: {
          id: orderDetail.service.id,
          serviceFee: rate,
        },
      } as OrderDetailInput;
    });

    return {
      services,
      signature: this.buildFeeInputSignature(),
    };
  }

  private loadAmendFeePreview$(services: OrderDetailInput[]) {
    this.form.setErrors({
      billingPreviewPending: true,
    });
    this.cdr.markForCheck();

    return this.jobApi.getAmendFeePreview$(this.data.job.id, services).pipe(
      tap((preview) => {
        if (!preview) {
          this.form.setErrors({
            billingPreviewUnavailable: true,
          });
          this.cdr.markForCheck();
          return;
        }

        this.form.controls.subtotal.setValue(preview.subtotal, {emitEvent: false});
        this.form.controls.tax.setValue(preview.tax, {emitEvent: false});
        this.form.controls.officialFees.setValue(preview.officialFee, {emitEvent: false});
        this.form.controls.total.setValue(preview.totalAmount, {emitEvent: false});

        if (preview.requiresStripeValidation && !preview.stripeValidation.isValid) {
          this.form.setErrors({
            stripeChargeValidation: {
              reason: preview.stripeValidation.reason || 'The Stripe charge for this billing is invalid.',
            },
          });
        } else {
          this.clearBillingPreviewErrors();
        }

        this.cdr.markForCheck();
      }),
      catchError(() => {
        this.form.setErrors({
          billingPreviewUnavailable: true,
        });
        this.cdr.markForCheck();

        return EMPTY;
      }),
    );
  }

  private clearBillingPreviewErrors(): void {
    this.form.setErrors(null);
  }
}
